import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Truck, LogIn, Shield, Mail, ArrowLeft } from 'lucide-react';

// Funcții pentru gestionarea device token
const getDeviceToken = () => localStorage.getItem('device_token');
const setDeviceToken = (token) => localStorage.setItem('device_token', token);

export default function LoginPage({ auth }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // 2FA State
  const [show2FA, setShow2FA] = useState(false);
  const [twoFAUserId, setTwoFAUserId] = useState(null);
  const [twoFAEmailHint, setTwoFAEmailHint] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);

  // Safety check for auth prop during logout transitions (after hooks!)
  if (!auth) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Se încarcă...</div>
      </div>
    );
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const deviceToken = getDeviceToken();
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password,
      }, {
        headers: {
          'X-Device-Token': deviceToken || '',
        }
      });

      // Verifică dacă necesită 2FA
      if (response.data.requires_2fa) {
        setTwoFAUserId(response.data.user_id);
        setTwoFAEmailHint(response.data.email_hint);
        setShow2FA(true);
        toast.info('Verificare necesară pentru dispozitiv nou');
        return;
      }

      // Verifică avertisment 2FA dezactivat
      if (response.data.warning) {
        toast.warning(response.data.warning, { duration: 8000 });
      }

      // Login normal - fără 2FA sau dispozitiv memorat
      completeLogin(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Autentificare eșuată');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async () => {
    setSendingCode(true);
    try {
      await axios.post(`${API}/auth/2fa/send-code`, {
        user_id: twoFAUserId
      });
      setCodeSent(true);
      toast.success('Codul a fost trimis pe email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nu s-a putut trimite codul');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/2fa/verify`, {
        user_id: twoFAUserId,
        code: verificationCode,
        remember_device: rememberDevice
      });

      // Salvează device token dacă a fost returnat
      if (response.data.device_token) {
        setDeviceToken(response.data.device_token);
      }

      completeLogin(response.data);
      toast.success('Autentificare reușită!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cod invalid sau expirat');
    } finally {
      setLoading(false);
    }
  };

  const completeLogin = (data) => {
    // Use auth.login which sets both localStorage and state
    auth.login(data.token, data.user);
    
    // Toți în afară de șoferi merg la dashboard-ul admin
    if (data.user.role !== 'driver') {
      navigate('/admin', { replace: true });
    } else {
      navigate('/driver', { replace: true });
    }
  };

  const handleBackToLogin = () => {
    setShow2FA(false);
    setTwoFAUserId(null);
    setTwoFAEmailHint('');
    setVerificationCode('');
    setCodeSent(false);
  };

  // Ecran 2FA
  if (show2FA) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Card className="w-full max-w-md shadow-2xl" data-testid="2fa-card">
          <CardHeader className="space-y-4 text-center pb-6">
            <div className="mx-auto w-20 h-20 bg-gradient-to-br from-green-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold">Verificare în 2 pași</CardTitle>
            <CardDescription className="text-sm">
              Dispozitiv nou detectat. Verifică-ți identitatea.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!codeSent ? (
              // Pasul 1: Trimite codul
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-blue-900">Email de verificare</p>
                      <p className="text-xs text-blue-700">{twoFAEmailHint}</p>
                    </div>
                  </div>
                </div>
                <Button
                  onClick={handleSendCode}
                  disabled={sendingCode}
                  className="w-full h-12 bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700"
                  data-testid="send-code-button"
                >
                  {sendingCode ? 'Se trimite...' : 'Trimite codul pe email'}
                </Button>
              </div>
            ) : (
              // Pasul 2: Introdu codul
              <form onSubmit={handleVerifyCode} className="space-y-4">
                <div className="p-3 bg-green-50 rounded-lg border border-green-200 text-center">
                  <p className="text-sm text-green-800">
                    ✓ Codul a fost trimis la <strong>{twoFAEmailHint}</strong>
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="code" className="text-sm font-medium">Cod de verificare</Label>
                  <Input
                    id="code"
                    type="text"
                    placeholder="Introdu codul din 6 cifre"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="h-12 text-center text-2xl tracking-widest font-mono"
                    maxLength={6}
                    autoFocus
                    data-testid="verification-code-input"
                  />
                </div>

                <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg">
                  <Checkbox
                    id="remember"
                    checked={rememberDevice}
                    onCheckedChange={setRememberDevice}
                    data-testid="remember-device-checkbox"
                  />
                  <label htmlFor="remember" className="text-sm text-slate-700 cursor-pointer">
                    Memorează acest dispozitiv pentru 30 zile
                  </label>
                </div>

                <Button
                  type="submit"
                  disabled={loading || verificationCode.length !== 6}
                  className="w-full h-12 bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700"
                  data-testid="verify-code-button"
                >
                  {loading ? 'Se verifică...' : 'Verifică și conectează-te'}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleSendCode}
                  disabled={sendingCode}
                  className="w-full text-sm"
                >
                  {sendingCode ? 'Se trimite...' : 'Retrimite codul'}
                </Button>
              </form>
            )}

            <Button
              type="button"
              variant="outline"
              onClick={handleBackToLogin}
              className="w-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Înapoi la login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Ecran login normal
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <Card className="w-full max-w-md shadow-2xl" data-testid="login-card">
        <CardHeader className="space-y-4 text-center pb-8">
          <div className="mx-auto w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
            <Truck className="w-10 h-10 text-white" />
          </div>
          <CardTitle className="text-3xl font-bold" style={{ fontWeight: 600 }}>C.Manager</CardTitle>
          <CardDescription className="text-base">Autentifică-te pentru a accesa panoul</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-sm font-medium">Utilizator</Label>
              <Input
                id="username"
                name="username"
                data-testid="username-input"
                type="text"
                placeholder="Introdu numele de utilizator"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">Parolă</Label>
              <Input
                id="password"
                name="password"
                data-testid="password-input"
                type="password"
                placeholder="Introdu parola"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                className="h-11"
              />
            </div>
            <Button
              type="submit"
              data-testid="login-button"
              disabled={loading}
              className="w-full h-12 text-base font-medium bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Se autentifică...' : 'Autentificare'}
            </Button>
            <div className="text-center mt-4">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
              >
                Am uitat parola
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
